import {
    ReportSettingRowVisibility,
    STATEMENT_ROW_VISIBILITY_VALUES,
    getReportSettingsRowVisibilityOptions,
} from "@common/types/reports/report-settings";
import { LAYOUT_TEXT_FIELD_KEYS } from "@common/schemas/domains/reportSettings/layout-setting.schema";
import { ReportSettingsDraftSpot } from "./reportSettingsDrafts";

interface SelectOption<T> {
    value: T;
    label: string;
    tooltip?: string;
}

export const ROW_VISIBILITY_OPTIONS: Array<
    SelectOption<ReportSettingRowVisibility>
> = [
    {
        value: ReportSettingRowVisibility.ALL_ACCOUNTS,
        label: "All accounts",
        tooltip:
            "All the accounts in the chart of accounts, including accounts that have never been used",
    },
    {
        value: ReportSettingRowVisibility.WITH_ACTIVITY,
        label: "Has activity",
        tooltip:
            "Accounts with any transaction, including those that net to zero",
    },
    {
        value: ReportSettingRowVisibility.NON_ZERO,
        label: "Has a balance",
        tooltip: "Accounts with a nonzero balance",
    },
    {
        value: ReportSettingRowVisibility.GL_STANDARD,
        label: "Standard",
        tooltip:
            "Accounts with transactions and balance sheet accounts carrying a balance forward",
    },
];

const optionsForValues = (
    values: ReportSettingRowVisibility[],
): Array<SelectOption<ReportSettingRowVisibility>> =>
    values.map((value) => {
        const option = ROW_VISIBILITY_OPTIONS.find(
            (candidate) => candidate.value === value,
        );
        return {
            value,
            label: option?.label ?? String(value),
            tooltip: option?.tooltip,
        };
    });

export const getRowVisibilityOptions = (
    reportKey: ReportSettingsDraftSpot["reportKey"],
): Array<SelectOption<ReportSettingRowVisibility>> =>
    optionsForValues(
        getReportSettingsRowVisibilityOptions(reportKey) ??
            STATEMENT_ROW_VISIBILITY_VALUES,
    );

export type LayoutTextFieldKey = (typeof LAYOUT_TEXT_FIELD_KEYS)[number];

export interface TextFieldConfig {
    key: LayoutTextFieldKey;
    label: string;
    isOptional?: boolean;
    placeholder: string;
}

export const BRANDING_TEXT_FIELDS: TextFieldConfig[] = [
    {
        key: "header",
        label: "Header",
        isOptional: true,
        placeholder: "Insert text or variable",
    },
    { key: "title", label: "Title", placeholder: "Insert text or variable" },
    {
        key: "subtitle",
        label: "Subtitle",
        placeholder: "Insert text or variable",
    },
    {
        key: "subtitle2",
        label: "Subtitle 2",
        placeholder: "Insert text or variable",
    },
    { key: "footer", label: "Footer", placeholder: "Insert text or variable" },
    {
        key: "memo",
        label: "Memo",
        isOptional: true,
        placeholder: "Insert text or variable",
    },
];

export const WATERMARK_FIELD: TextFieldConfig = {
    key: "watermarkText",
    label: "Watermark",
    isOptional: true,
    placeholder: "Type to enable",
};

export const labelFor = <T>(
    options: Array<SelectOption<T>>,
    value: T,
): string =>
    options.find((option) => option.value === value)?.label ?? String(value);
